// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  InternalCreateTargetPortfolioRequestBody,
  InternalProposalOperation,
  TargetPortfolioOperation,
  TargetPortfolioPosition,
  TargetPortfolioRequest,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { CASH_ACCOUNT_IBAN } from '@shared/data-access/utils/nuvara-inversis-institution';

export const TARGET_PORTFOLIO_CASH_TICKER = '$CASH' as const;

const CASH_CURRENCY_CODES = new Set(['EUR', 'USD']);

export const isCashIdentifier = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  if (value === TARGET_PORTFOLIO_CASH_TICKER || value === CASH_ACCOUNT_IBAN) {
    return true;
  }

  return CASH_CURRENCY_CODES.has(value.toUpperCase());
};

export const toCashLegIdentifier = (
  value: string | null | undefined,
): string | null | undefined =>
  isCashIdentifier(value) ? TARGET_PORTFOLIO_CASH_TICKER : value;

const mapOperation = (
  operation: InternalProposalOperation,
): TargetPortfolioOperation => {
  const fromTicker =
    operation.operation_type === 'buy'
      ? toCashLegIdentifier(operation.from_ticker)
      : operation.from_ticker;
  const fromIsin =
    operation.operation_type === 'buy'
      ? toCashLegIdentifier(operation.from_isin)
      : operation.from_isin;
  const toTicker =
    operation.operation_type === 'sell'
      ? toCashLegIdentifier(operation.to_ticker)
      : operation.to_ticker;
  const toIsin =
    operation.operation_type === 'sell'
      ? toCashLegIdentifier(operation.to_isin)
      : operation.to_isin;

  return {
    input_type: operation.input_type ?? 'shares',
    operation_type: operation.operation_type,
    from_iban: operation.from_iban,
    from_ticker: fromTicker,
    from_isin: fromIsin,
    to_iban: operation.to_iban,
    to_ticker: toTicker,
    to_isin: toIsin,
    quantity: operation.quantity,
    to_price: operation.price,
    amount: operation.amount,
  };
};

const mapPosition = (
  position: InternalCreateTargetPortfolioRequestBody['current_portfolio'][number],
): TargetPortfolioPosition => ({
  iban: position.iban,
  ticker: position.ticker,
  isin: position.isin,
  quantity: position.quantity,
  amount: position.amount,
});

/**
 * Maps the internal target-portfolio request (proposal operations with `price` and
 * `institution_id`) to the advisory simulate wire body.
 */
export const mapTargetPortfolioRequest = (
  params: InternalCreateTargetPortfolioRequestBody,
): TargetPortfolioRequest => ({
  current_portfolio: params.current_portfolio.map(mapPosition),
  operations: params.operations.map(mapOperation),
});
